import React, { useState } from 'react'
import { supabase, supabaseBackgroundAuth } from '../lib/supabase'
import { getSignupEmailRedirectUrl } from '../lib/authRedirects'
import ToggleSwitch from './ToggleSwitch'

const CreateCustomerModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    businessName: '',
    password: '',
    confirmPassword: '',
    automationEnabled: false,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      setLoading(false)
      return
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters')
      setLoading(false)
      return
    }

    try {
      // Get current admin user
      const { data: currentUser } = await supabase.auth.getUser()
      if (!currentUser?.user) {
        throw new Error('Admin authentication required')
      }

      // Check if a user with this email already exists in the app-level users table
      const { data: existingUser, error: existingUserError } = await supabase
        .from('users')
        .select('id')
        .eq('email', formData.email)
        .maybeSingle()

      if (existingUserError) {
        console.error('Error checking existing user:', existingUserError)
      }

      if (existingUser) {
        setError('A user with this email address already exists')
        setLoading(false)
        return
      }

      // Create the customer auth user using the background client so the
      // current admin session is not affected.
      const signupEmailRedirectTo = getSignupEmailRedirectUrl()
      const { data: authData, error: authError } = await supabaseBackgroundAuth.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: signupEmailRedirectTo,
          data: {
            role: 'customer',
          },
        },
      })

      if (authError) {
        throw authError
      }

      if (!authData?.user) {
        throw new Error('Failed to create user account')
      }

      // Create user record in users table. In some environments, a database trigger
      // may already insert into public.users when a new Auth user is created. In
      // that case, this insert can raise a Postgres unique-violation error
      // (code 23505) for the email. We treat that specific case as benign and
      // continue, since it simply means the user row already exists.
      const { error: userError } = await supabase
        .from('users')
        .insert({
          id: authData.user.id,
          email: formData.email,
          role: 'customer'
        })

      if (userError && userError.code !== '23505') {
        throw userError
      }

      // Create business record and return the full inserted row
      const { data: businessRecord, error: businessError } = await supabase
        .from('businesses')
        .insert({
          business_name: formData.businessName,
          owner_email: formData.email,
          admin_id: currentUser.user.id
        })
        .select('*')
        .single()

      if (businessError) {
        throw businessError
      }

      const automationPayload = {
        business_id: businessRecord.id,
        is_enabled: !!formData.automationEnabled,
        enabled_at: formData.automationEnabled ? new Date().toISOString() : null,
        enabled_by: formData.automationEnabled ? currentUser.user.id : null,
      }

      const { error: automationError } = await supabase
        .from('business_automation_profiles')
        .upsert(automationPayload, { onConflict: 'business_id' })

      if (automationError) {
        throw automationError
      }

      // Reset form
      setFormData({
        email: '',
        businessName: '',
        password: '',
        confirmPassword: '',
        automationEnabled: false,
      })

      // Pass the newly created business record back to parent so it can update immediately
      onSuccess({
        ...businessRecord,
        automation_profile: automationPayload,
      })
      onClose()
    } catch (err) {
      console.error('Create customer error:', err)
      
      // Provide specific error messages based on error type
      if (err.code === '23505') {
        // Postgres unique violation (e.g., primary key or unique email)
        setError('A user with this email address already exists')
      } else if (err.message?.includes('User already registered')) {
        setError('A user with this email address already exists')
      } else if (err.message?.includes('Invalid email')) {
        setError('Please enter a valid email address')
      } else if (err.message?.includes('Password')) {
        setError('Password must be at least 6 characters long')
      } else if (err.message?.includes('Admin authentication required')) {
        setError('You must be logged in as an admin to create customers')
      } else {
        setError(err.message || 'Failed to create customer account. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">Create New Customer</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="customer@example.com"
            />
          </div>

          <div>
            <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
              Business Name
            </label>
            <input
              type="text"
              id="businessName"
              name="businessName"
              value={formData.businessName}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Acme Corporation"
            />
          </div>

          <ToggleSwitch
            id="automationEnabled"
            label="Running Automation?"
            description="Enable this if the customer’s automation package should be activated immediately."
            checked={formData.automationEnabled}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                automationEnabled: value,
              }))
            }
          />

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="••••••••"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="••••••••"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateCustomerModal